import { useMemo } from 'react';
import { useParams } from 'react-router';

import {
  useQuantzData,
  QuantzOperation,
  QuantzProperty,
  QuantzQueryCacheDuration,
} from '@hotjar/quantz';
import type { QuantzCategoryDataItem } from '@hotjar/quantz';
import { HEATMAP_DEVICE_TYPES, mapHeatmapParamsToGlobalFilters } from '@hotjar/heatmaps';
import { useHeatmapParams } from '@hotjar/webapp/domains/heatmaps/hooks/useHeatmapParams';

const { DESKTOP, TABLET, PHONE } = HEATMAP_DEVICE_TYPES;

type IntermediateSessionCount = Record<string, number>;

interface DeviceSessionCounts {
  [DESKTOP]: number;
  [TABLET]: number;
  [PHONE]: number;
}

interface DeviceSessions {
  sessions: DeviceSessionCounts;
  isFetching: boolean;
  isError: boolean;
}

export function useGetDeviceSessions(): DeviceSessions {
  const { siteId } = useParams();

  const { params } = useHeatmapParams({
    siteId: Number(siteId),
  });

  const globalFilters = useMemo(() => mapHeatmapParamsToGlobalFilters(params, true), [params]);

  const { isFetching, data, isError } = useQuantzData(
    {
      sessionFilters: globalFilters,
      siteId: Number(siteId),
      operation: QuantzOperation.COUNT,
      groupBy: QuantzProperty.DEVICE,
    },
    true,
    QuantzQueryCacheDuration.Mid,
  );

  const sessions = {
    [DESKTOP]: 0,
    [TABLET]: 0,
    [PHONE]: 0,
  };

  if (
    data?.data?.resultSeries &&
    data.data.resultSeries[0].key === 'device' &&
    data.data.resultSeries[0].values.length
  ) {
    const intermediateSessions: IntermediateSessionCount = {};
    const results: QuantzCategoryDataItem[] = data.data.resultSeries[0]
      .values as QuantzCategoryDataItem[];

    results.forEach(({ category, value }) => {
      intermediateSessions[category] = value;
    });

    if (DESKTOP in intermediateSessions) {
      sessions[DESKTOP] = intermediateSessions[DESKTOP];
    }

    if (TABLET in intermediateSessions) {
      sessions[TABLET] = intermediateSessions[TABLET];
    }

    if (PHONE in intermediateSessions) {
      sessions[PHONE] = intermediateSessions[PHONE];
    }
  }

  return { isFetching, sessions, isError };
}
